import ReactGA from "react-ga4"
import { app, translate } from "./app"
import type { AgentServiceFilters } from "./agent-service-filters"
import { getAnalyticsConsent } from "./components/analytics"

const SWITCHBOARD_COUNTRY = 42

const MAX_PARAM_LENGTH = 100
const MAX_LIST_ITEMS = 10
const MAX_CHAT_CHUNKS = 40
const DEBUG_STORAGE_KEY = "signpost-analytics-debug"

export type MapSurface = "homepage_map" | "service_map_page" | "other"

export type ServiceClickSource =
  | "map_marker"
  | "map_popup"
  | "map_card"
  | "list_card"
  | "chat_card"

export type EventSource = ServiceClickSource | "ai_chat"

export type FilterSource = "filters_panel" | "ai_chat"

export type ZeroResultSearchType = "filters" | "ai_chat" | "list_search"

export type MapFilterType =
  | "serviceTypes"
  | "provider"
  | "Populations"
  | "Accessibility"
  | "location"

export function isMapAnalyticsEnabled(): boolean {
  return app.country === SWITCHBOARD_COUNTRY
}

export function getMapSurface(): MapSurface {
  if (typeof window === "undefined") return "other"
  const path = window.location.pathname.toLowerCase()
  if (path.includes("/service-map")) return "service_map_page"
  const segments = path.split("/").filter(Boolean)
  if (segments.length <= 1) return "homepage_map"
  return "other"
}

function truncate(value: string): string {
  return value.length > MAX_PARAM_LENGTH
    ? value.slice(0, MAX_PARAM_LENGTH - 1)
    : value
}

function joinLabels(labels: string[]): string {
  const cleaned = labels.map((label) => label.trim()).filter(Boolean)
  return truncate(cleaned.slice(0, MAX_LIST_ITEMS).join(" | "))
}

function isDebugEnabled(): boolean {
  if (typeof window === "undefined") return false
  try {
    return window.localStorage.getItem(DEBUG_STORAGE_KEY) === "1"
  } catch {
    return false
  }
}

function sendEvent(name: string, params: Record<string, unknown>) {
  if (!isMapAnalyticsEnabled()) return
  if (getAnalyticsConsent() !== "accepted") return

  const payload: Record<string, unknown> = {
    surface: getMapSurface(),
    locale: app.locale,
  }

  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return
    payload[key] = typeof value === "string" ? truncate(value) : value
  })

  if (isDebugEnabled()) {
    console.log("[analytics]", name, payload)
  }

  if (!ReactGA.isInitialized) return

  try {
    ReactGA.event(name, payload)
  } catch (error) {
    console.log("Could not send analytics event", name, error)
  }
}

function localizedName(name?: LocalizableText): string {
  if (!name) return ""
  return name["en-US"] || translate(name)
}

function categoryLabel(id?: number | null): string {
  if (id == null) return ""
  return localizedName(app.data.categories.categories?.[id]?.name)
}

function regionLabel(id?: number | null): string {
  if (id == null) return ""
  const match = Object.values(app.regions || []).find(
    (region) => region.id === id,
  )
  return localizedName(match?.name)
}

function cityLabel(id?: number | null): string {
  if (id == null) return ""
  const match = Object.values(app.cities || []).find((city) => city.id === id)
  return localizedName(match?.name)
}

export function getServiceLabel(service?: Service): string {
  return localizedName(service?.name)
}

export function getServiceCategoryNames(service?: Service): string[] {
  if (!service?.categories?.length) return []
  return service.categories.map((id) => categoryLabel(id)).filter(Boolean)
}

export function getServiceProviderName(service?: Service): string {
  if (service?.provider == null) return ""
  return localizedName(app.data.providers?.[service.provider]?.name)
}

function resolveFilterLabels(
  filterType: MapFilterType,
  values: (string | number)[],
): string[] {
  return values
    .map((value) => {
      const isChild = typeof value === "string" && value.includes("-")
      const id = isChild ? Number(String(value).split("-")[1]) : Number(value)
      if (!Number.isFinite(id)) return ""

      switch (filterType) {
        case "serviceTypes":
          return isChild
            ? localizedName(app.data.categories.subCategories?.[id]?.name)
            : categoryLabel(id)
        case "provider":
          return localizedName(app.data.providers?.[id]?.name)
        case "Populations":
          return localizedName(app.data.categories.populations?.[id]?.name)
        case "Accessibility":
          return localizedName(app.data.categories.accesibility?.[id]?.name)
        case "location":
          return isChild ? cityLabel(id) : regionLabel(id)
        default:
          return ""
      }
    })
    .filter(Boolean)
}

export function trackMapFilterApplied(params: {
  filterType: MapFilterType
  values: (string | number)[]
  resultCount: number
  view: "map" | "list"
}) {
  const { filterType, values, resultCount, view } = params
  const isCleared = !values.length || values[0] === -1
  const labels = isCleared ? [] : resolveFilterLabels(filterType, values)

  emitFilterEvent({
    filterType,
    labels,
    filterAction: isCleared ? "cleared" : "applied",
    filterSource: "filters_panel",
    resultCount,
    view,
  })

  if (filterType === "serviceTypes" && labels.length) {
    emitCategoryViews(labels, "filters_panel")
  }
}

function emitFilterEvent(params: {
  filterType: MapFilterType
  labels: string[]
  filterAction: "applied" | "cleared"
  filterSource: FilterSource
  resultCount: number
  view?: "map" | "list"
  searchTerm?: string
}) {
  const {
    filterType,
    labels,
    filterAction,
    filterSource,
    resultCount,
    view,
    searchTerm,
  } = params

  sendEvent("map_filter_applied", {
    filter_type: filterType,
    filter_action: filterAction,
    filter_source: filterSource,
    filter_values: joinLabels(labels),
    filter_value_count: labels.length,
    result_count: resultCount,
    view,
    search_term: searchTerm,
  })
}

function emitCategoryViews(
  categories: string[],
  source: EventSource | FilterSource,
  serviceId?: number,
) {
  categories.slice(0, MAX_LIST_ITEMS).forEach((category) => {
    sendEvent("service_category_view", {
      service_category: category,
      service_id: serviceId,
      source,
    })
  })
}

function emitResourceCategoryAccess(
  categories: string[],
  source: EventSource | FilterSource,
  serviceId?: number,
) {
  categories.slice(0, MAX_LIST_ITEMS).forEach((category) => {
    sendEvent("resource_category_access", {
      resource_category: category,
      service_id: serviceId,
      source,
    })
  })
}

export function trackAgentFiltersApplied(params: {
  filters: AgentServiceFilters
  resultCount: number
  searchTerm?: string
}) {
  const { filters, resultCount, searchTerm } = params

  const categoryName = categoryLabel(filters.category)
  const locationNames = [
    regionLabel(filters.region),
    cityLabel(filters.city),
  ].filter(Boolean)
  const providerName = filters.serviceName?.trim() ?? ""

  if (categoryName) {
    emitFilterEvent({
      filterType: "serviceTypes",
      labels: [categoryName],
      filterAction: "applied",
      filterSource: "ai_chat",
      resultCount,
      searchTerm,
    })
    emitCategoryViews([categoryName], "ai_chat")
    emitResourceCategoryAccess([categoryName], "ai_chat")
  }

  if (locationNames.length) {
    emitFilterEvent({
      filterType: "location",
      labels: locationNames,
      filterAction: "applied",
      filterSource: "ai_chat",
      resultCount,
      searchTerm,
    })
  }

  if (providerName) {
    emitFilterEvent({
      filterType: "provider",
      labels: [providerName],
      filterAction: "applied",
      filterSource: "ai_chat",
      resultCount,
      searchTerm,
    })
  }
}

export function trackAgentResultCategories(params: {
  services: Service[]
  searchTerm?: string
}) {
  const { services, searchTerm } = params
  if (!services.length) return

  const categories = Array.from(
    new Set(services.flatMap((service) => getServiceCategoryNames(service))),
  )
  if (!categories.length) return

  sendEvent("ai_result_categories", {
    service_categories: joinLabels(categories),
    category_count: categories.length,
    result_count: services.length,
    search_term: searchTerm,
  })
}

function chunkText(text: string, size = MAX_PARAM_LENGTH): string[] {
  if (!text) return [""]
  const chunks: string[] = []
  for (let i = 0; i < text.length; i += size) {
    chunks.push(text.slice(i, i + size))
  }
  return chunks
}

export function trackAiChatMessage(params: {
  role: "user" | "assistant"
  text: string
  exchangeId: string
  resultCount?: number
  language?: string
}) {
  const { role, text, exchangeId, resultCount, language } = params
  const allChunks = chunkText(text.trim())
  const truncated = allChunks.length > MAX_CHAT_CHUNKS
  const chunks = allChunks.slice(0, MAX_CHAT_CHUNKS)
  const chunkTotal = chunks.length

  chunks.forEach((chunk, index) => {
    sendEvent("ai_chat_message", {
      message_role: role,
      exchange_id: exchangeId,
      chunk_index: index + 1,
      chunk_total: chunkTotal,
      message_chunk: chunk,
      text_length: text.length,
      text_truncated: truncated ? 1 : 0,
      result_count: role === "assistant" ? resultCount : undefined,
      chat_language: language,
    })
  })
}

export function trackServiceClick(params: {
  service: Service
  source: ServiceClickSource
}) {
  const { service, source } = params
  const categories = getServiceCategoryNames(service)

  sendEvent("service_click", {
    service_id: service.id,
    service_name: getServiceLabel(service),
    provider_name: getServiceProviderName(service),
    service_categories: joinLabels(categories),
    source,
  })

  trackServiceCategoryView({ service, source })
}

export function trackServiceCategoryView(params: {
  service: Service
  source: ServiceClickSource
}) {
  const { service, source } = params
  emitCategoryViews(getServiceCategoryNames(service), source, service.id)
}

export function trackServiceDetailsClick(params: {
  service: Service
  source: ServiceClickSource
}) {
  const { service, source } = params
  const categories = getServiceCategoryNames(service)

  sendEvent("service_details_click", {
    service_id: service.id,
    service_name: getServiceLabel(service),
    provider_name: getServiceProviderName(service),
    service_categories: joinLabels(categories),
    source,
  })

  emitResourceCategoryAccess(categories, source, service.id)
}

export function trackZeroResults(params: {
  searchType: ZeroResultSearchType
  searchTerm?: string
  activeFilters?: string
  view?: "map" | "list"
}) {
  const { searchType, searchTerm, activeFilters, view } = params

  sendEvent("search_zero_results", {
    search_type: searchType,
    search_term: searchTerm,
    active_filters: activeFilters,
    view,
  })
}

export function describeActiveFilters(
  selected: Record<string, (string | number)[]>,
): string {
  const parts: string[] = []

  Object.entries(selected).forEach(([key, values]) => {
    if (!values?.length || values[0] === -1) return
    const labels = resolveFilterLabels(key as MapFilterType, values)
    if (labels.length) parts.push(`${key}: ${labels.join(", ")}`)
  })

  return truncate(parts.join(" ; "))
}
